/**
 * Read-only helpers for the in-memory access token. We NEVER trust these
 * unverified claims for authorization — the only use is reading which
 * subsidiary the Bearer token is scoped to, so the `X-Subsidiary-ID` header we
 * attach always matches the token (identity-service's UserSubsidiaryGuard
 * rejects any request whose token subsidiary ≠ request subsidiary).
 */

/** Decodes a JWT payload segment (base64url) without verifying the signature.
 *  Browser-only (uses `atob`). Returns the parsed claims, or null when the
 *  token is missing a payload or isn't valid JSON. */
function decodeJwtPayload(token: string): Record<string, unknown> | null {
  const segment = token.split('.')[1];
  if (!segment) return null;
  try {
    const base64 = segment.replace(/-/g, '+').replace(/_/g, '/');
    const padded = base64.padEnd(base64.length + ((4 - (base64.length % 4)) % 4), '=');
    return JSON.parse(atob(padded)) as Record<string, unknown>;
  } catch {
    return null;
  }
}

/** Subsidiary id the given access token is scoped to (its `subsidiaryId`
 *  claim), or null when the token is absent/malformed or carries no claim. */
export function getTokenSubsidiaryId(token: string | null): string | null {
  if (!token) return null;
  const claims = decodeJwtPayload(token);
  const subsidiaryId = claims?.subsidiaryId;
  return typeof subsidiaryId === 'string' && subsidiaryId !== '' ? subsidiaryId : null;
}

/**
 * The subsidiary a request is sent for — the `X-Subsidiary-ID` the subsidiary interceptor attaches:
 * the access token's claim, falling back to `activeTenantId` only when the token cannot be read.
 * Anything cached per subsidiary keys on this same value, so a cache entry always names the
 * subsidiary whose answer it holds.
 */
export function requestSubsidiaryId(state: {
  accessToken: string | null;
  user: { activeTenantId?: string | null } | null;
}): string | null {
  return getTokenSubsidiaryId(state.accessToken) ?? state.user?.activeTenantId ?? null;
}
